import { createFileRoute, useRouter } from "@tanstack/react-router";
import { EstimateWizard, type StudioAttachment } from "@/components/estimate/EstimateWizard";
import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { track } from "@/lib/analytics/track";

type Search = {
  project?: string;
  version?: string;
  image?: string;
};

export const Route = createFileRoute("/estimate")({
  head: () => ({
    meta: [
      { title: "Free Painting Estimate — Madison, WI | Michael's Painting" },
      { name: "description", content: "Get an instant estimate range for your residential or commercial painting project in seconds. Confirmed by an on-site visit." },
      { property: "og:title", content: "Get an Instant Painting Estimate" },
      { property: "og:description", content: "AI-assisted instant estimate with a real follow-up from our team." },
      { property: "og:url", content: "https://michaelspaintingwi.com/estimate" },
    ],
    links: [{ rel: "canonical", href: "https://michaelspaintingwi.com/estimate" }],
  }),
  validateSearch: (search: Record<string, unknown>): Search => ({
    project: typeof search.project === "string" ? search.project : undefined,
    version: typeof search.version === "string" ? search.version : undefined,
    image: typeof search.image === "string" ? search.image : undefined,
  }),
  component: EstimateRoute,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-32 text-center bg-[#030303] min-h-screen text-zinc-300">
        <p className="mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2.5 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="py-32 text-center text-zinc-400">Not found.</div>,
});

function EstimateRoute() {
  const { project, version, image } = Route.useSearch();
  const [attachment, setAttachment] = useState<StudioAttachment | undefined>(undefined);

  useEffect(() => {
    track.funnel("direct_estimate", "estimate_open", 0);
    track.event("estimate_open");
  }, []);

  useEffect(() => {
    if (!project && !image) return;
    try {
      const raw = typeof window !== "undefined" ? window.sessionStorage.getItem("mp_estimate_attachment") : null;
      const stored = raw ? JSON.parse(raw) as StudioAttachment : {};
      setAttachment({
        studioProjectId: project ?? stored.studioProjectId ?? null,
        studioVersionId: version ?? stored.studioVersionId ?? null,
        designImageUrl: image ?? stored.designImageUrl ?? null,
        selectedColors: stored.selectedColors ?? [],
        favoriteColors: stored.favoriteColors ?? [],
      });
    } catch {/* noop */}
  }, [project, version, image]);

  return (
    <section className="relative bg-[#030303] min-h-screen text-white">
      <div className="absolute top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-amber-500/10 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-24 relative">
        <header className="max-w-3xl mb-10 mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-[11px] font-medium tracking-widest uppercase text-amber-200 mb-5">
            <Sparkles className="size-3" /> AI Instant Estimate
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4">Get a real estimate in 60 seconds.</h1>
          <p className="text-zinc-400 text-lg">Answer a few quick questions and we'll generate an instant price range. A senior estimator confirms with a free on-site walkthrough.</p>
        </header>
        <EstimateWizard attachment={attachment} />
      </div>
    </section>
  );
}